import { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import Modal from '../core/Modal';

// Use raw client for auth operations
import { supabaseRaw as supabase } from '../../lib/supabase';
import { addCaughtError } from '../../utils/errorCapture';
import { authErrorMessage } from '../../utils/authFlow';
import { PASSWORD_MIN_LENGTH } from '../../utils/validationConstants';

// The password minimum is the one shared rule, checked as the person types; any other refusal is
// translated from Supabase's error code, raw text to the error log only
// (docs/briefs/2026-10-03-registration-confirm-first-addendum-2.md item 1).

const ResetPasswordModal = ({ visible = false, onClose = null }) => {
  const { t } = useTranslation('auth');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);             // an auth-namespace key, translated at render
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Sync submit wrapper — preventDefault must run before any await (see the form's note below).
  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  const showUpdateError = (err) => {
    const mapped = authErrorMessage(err, 'password');
    if (!mapped.known) addCaughtError(err?.message || String(err), 'ResetPasswordModal:password');
    setError(mapped.key);
  };

  const handleSubmit = async () => {
    setError(null);

    if (!password) {
      setError('password_required_error');
      return;
    }

    if (password.length < PASSWORD_MIN_LENGTH) {
      setError('password_min_length');
      return;
    }

    if (password !== confirmPassword) {
      setError('passwords_no_match_error');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        console.error('Password reset error:', updateError);
        showUpdateError(updateError);
        return;
      }

      console.log('✅ Password updated successfully');
      setSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err);
      showUpdateError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={visible}
      onClose={handleDone}
      closeOnEsc={false}
      closeOnBackdrop={false}
      panelClassName="rounded-3xl shadow-2xl w-full max-w-md mx-4 p-8"
      panelStyle={{ background: 'linear-gradient(135deg, #C94B9E 0%, #E05DBE 100%)' }}
    >

        {/* OurNet Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/images/logos/logo-ournet.png"
            alt="OurNet"
            style={{ width: '80px', height: '80px', objectFit: 'contain' }}
          />
        </div>

        {success ? (
          <>
            <h2 className="text-2xl font-bold text-center mb-3" style={{ color: 'white' }}>
              {t('reset_success_title')}
            </h2>
            <p className="text-center mb-6" style={{ color: 'white', fontSize: '14px' }}>
              {t('reset_success_message')}
            </p>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleDone}
                className="px-8 py-3 rounded-lg font-semibold transition-colors"
                style={{
                  background: '#5DBEA3',
                  border: 'none',
                  color: 'white',
                  minWidth: '150px'
                }}
              >
                {t('common:ok').toUpperCase()}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-center mb-3" style={{ color: 'white' }}>
              {t('reset_password_title')}
            </h2>
            <div className="text-center mb-6" style={{ color: 'white', fontSize: '14px', lineHeight: '1.5' }}>
              <p>{t('reset_password_instructions')}</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {t(error, { min: PASSWORD_MIN_LENGTH })}
              </div>
            )}

            {/* 2026-09-25 sign-in-forms build: the fields and the primary action sit inside a real
                <form> so Chrome and Android autofill see a submission and offer to save or fill.
                onSubmit calls a SYNCHRONOUS wrapper whose first statement is preventDefault() —
                passing the async handler directly would let the browser submit natively as GET and
                write the credentials into the URL. noValidate keeps the app's own translated
                validation authoritative; type="email" would otherwise raise the browser's own
                bubble and the handler would never run. */}
            <form onSubmit={handleFormSubmit} noValidate>
              <div className="space-y-4 mb-6">
                <div>
                  <label htmlFor="reset-password" className="block text-sm font-medium mb-2" style={{ color: 'white' }}>
                    {t('new_password_label')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      autoComplete="new-password"
                      id="reset-password"
                      aria-describedby={password && password.length < PASSWORD_MIN_LENGTH ? 'reset-password-hint' : undefined}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(null); }}
                      placeholder={t('password_placeholder')}
                      className="w-full px-4 py-3 pr-12 border-0 rounded-lg focus:ring-2 focus:ring-white focus:outline-none"
                      style={{
                        fontSize: '14px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ fontSize: '20px', background: 'transparent', border: 'none', cursor: 'pointer' }}
                    >
                      {showPassword ? '\u{1F441}\uFE0F' : '\u{1F441}\uFE0F\u200D\u{1F5E8}\uFE0F'}
                    </button>
                  </div>
                  {/* As the person types: the shared minimum (addendum 2 item 1). */}
                  {password && password.length < PASSWORD_MIN_LENGTH && (
                    <p id="reset-password-hint" className="text-sm mt-1" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                      {t('password_min_length', { min: PASSWORD_MIN_LENGTH })}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="reset-confirm-password" className="block text-sm font-medium mb-2" style={{ color: 'white' }}>
                    {t('confirm_password_label')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      autoComplete="new-password"
                      id="reset-confirm-password"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
                      placeholder={t('confirm_password_placeholder')}
                      className="w-full px-4 py-3 pr-12 border-0 rounded-lg focus:ring-2 focus:ring-white focus:outline-none"
                      style={{
                        fontSize: '14px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ fontSize: '20px', background: 'transparent', border: 'none', cursor: 'pointer' }}
                    >
                      {showPassword ? '\u{1F441}\uFE0F' : '\u{1F441}\uFE0F\u200D\u{1F5E8}\uFE0F'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  type="button"
                  onClick={handleDone}
                  className="px-8 py-3 rounded-lg font-semibold transition-colors"
                  style={{
                    background: 'white',
                    border: '2px solid white',
                    color: '#C94B9E',
                    minWidth: '150px'
                  }}
                >
                  {t('common:cancel').toUpperCase()}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 rounded-lg font-semibold text-white transition-colors disabled:opacity-50"
                  style={{
                    background: '#5DBEA3',
                    border: 'none',
                    minWidth: '150px'
                  }}
                >
                  {loading ? t('saving_button').toUpperCase() : t('submit_button').toUpperCase()}
                </button>
              </div>
            </form>
          </>
        )}

    </Modal>
  );
};

export default ResetPasswordModal;
